import { useState } from 'react';
import { 
  BookOpen, 
  Search, 
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

const KB_CATEGORIES = [
  { id: 'all', label: 'All Articles' },
  { id: 'soc', label: 'SOC Guidelines' },
  { id: 'mitre', label: 'MITRE Guidelines' },
  { id: 'rule_dev', label: 'Rule Development' },
  { id: 'uat', label: 'UAT Guidelines' },
  { id: 'production', label: 'Production Guidelines' }
];

const KB_ARTICLES = [
  {
    id: 'kb-1',
    category: 'soc',
    categoryLabel: 'SOC Guidelines',
    title: 'The 15-Stage SOC Use Case Governance Standard',
    summary: 'A complete architectural walkthrough of how detection rules transition through specification, validation, soak, dual approval, and production release.',
    content: `The SOC Use Case Lifecycle enforces strict quality, false-positive suppression, and verification standards across 15 chronological gates:
1. CREATED: Initial requirement specification by author.
2. VALIDATED: Lead review of technical feasibility.
3. COORDINATION_PENDING: Dispatch to coordination leads.
4. SHIFT_LEAD_ASSIGNMENT: Shift lead evaluates log availability.
5. IN_PROGRESS: Dedicated engineer writes query logic (3-Day Dev SLA).
6. SHIFT_LEAD_VERIFICATION: Exactly 2 validations executed (True Positive & Benign Baseline).
7. SHIFT_LEAD_REVIEW: Code sign-off and suppression verification (1-Day SLA).
8. TECHNICAL_REVIEW: Senior peer architectural sign-off.
9. UAT_APPROVED: Clearance for soak deployment.
10. UAT_DEPLOYED: Mandatory 5 artifacts provided (Screenshot, Entity Mapping, MITRE, Schedule, Lookback).
11. WEEKLY_REVIEW_COMPLETED: 7-day alert fidelity assessment.
12. POST_MEETING_REPORT: Documentation of healthy rule execution.
13. FINAL_APPROVED: Executive authorization by Aravind.
14. PRODUCTION_APPROVED: Secondary authorization by Logesh & Heman.
15. PRODUCTION_DEPLOYED: Live release in 24/7 security monitoring tenants.`
  },
  {
    id: 'kb-2',
    category: 'soc',
    categoryLabel: 'SOC Guidelines',
    title: 'Strict Concurrency Invariant: One Task Per Engineer',
    summary: 'Engineers may not be assigned to concurrent active development rules to preserve focus and adhere to the 3-day development SLA.',
    content: `Key Governance Invariant:
A Detection Engineer can only have ONE rule in active development ('ENGINEER_ASSIGNED', 'IN_PROGRESS', 'TESTING', 'READY_FOR_VALIDATION') at any given time.
If a Shift Lead attempts to assign a second rule to a busy engineer, the backend workflow engine immediately aborts the transition with an actionable violation error. This enforces delivery within the 3 working days SLA window.`
  },
  {
    id: 'kb-3',
    category: 'mitre',
    categoryLabel: 'MITRE Guidelines',
    title: 'MITRE ATT&CK Enterprise Framework Alignment',
    summary: 'Best practices for categorizing detections by Tactic (TA0001-TA0043) and Technique (Txxxx) with sub-technique granularity.',
    content: `Every detection use case must be mapped to at least one primary MITRE ATT&CK Tactic and Technique.
Examples:
- Credential Access (TA0006) -> Steal or Forge Kerberos Tickets: Kerberoasting (T1558.003)
- Defense Evasion (TA0005) -> Masquerading: Match Legitimate Name (T1036.005)
- Lateral Movement (TA0008) -> Remote Services: SMB/Windows Admin Shares (T1021.002)
- Persistence (TA0003) -> Scheduled Task/Job: Scheduled Task (T1053.005)`
  },
  {
    id: 'kb-4',
    category: 'rule_dev',
    categoryLabel: 'Rule Development',
    title: 'KQL & SIEM Query Development Standards',
    summary: 'Performance optimization, indexing awareness, and false positive suppression standards for SIEM detection logic.',
    content: `When authoring detection queries (Kusto Query Language or Splunk SPL):
1. Always scope tables by time window first ('where TimeGenerated >= ago(15m)').
2. Project only relevant entity fields (Account, Host, IP, ProcessCommandLine).
3. Implement statistical thresholds rather than raw occurrence triggers where noise is expected.
4. Document benign software vendors and known baseline maintenance tasks to avoid recurring analyst alert fatigue.`
  },
  {
    id: 'kb-5',
    category: 'uat',
    categoryLabel: 'UAT Guidelines',
    title: 'Mandatory 5 Artifacts for Step 10 UAT Deployment',
    summary: 'The 5 artifacts that must be attached before launching the automated 7-day soak evaluation period.',
    content: `Before a rule can be deployed into UAT (Step 10), the deployer must attach all 5 mandatory artifacts:
1. Alert Proof Screenshot: Visual proof of alert firing in test environment.
2. Entity Mapping: Explicit mapping of SIEM account, host, and IP entities.
3. MITRE ATT&CK Matrix: Tactic and technique mapping confirmation.
4. Rule Schedule: Execution interval (e.g. Every 5 minutes).
5. Lookback Duration: Query lookback window (e.g. Past 15 minutes).`
  },
  {
    id: 'kb-6',
    category: 'uat',
    categoryLabel: 'UAT Guidelines',
    title: '7-Day UAT Soak Window Protocol',
    summary: 'Evaluating noise, telemetry spikes, and analyst feedback during the 7-day soak period prior to the Weekly Review meeting.',
    content: `Upon deployment to UAT, an automatic 7-day review timer is set.
During this window:
- Real production telemetry flows through the detection logic.
- False positive rates are monitored.
- On Day 7, the Weekly Review meeting is conducted by Praveen & Seshadri and findings are compiled into the Post Meeting Report (PMR).`
  },
  {
    id: 'kb-7',
    category: 'production',
    categoryLabel: 'Production Guidelines',
    title: 'Dual Executive Sign-Off & Release Gate',
    summary: 'Understanding the invariant dual approval security gate: Step 13 (Aravind) and Step 14 (Logesh & Heman) before live release.',
    content: `Production Release Security Gate:
No detection rule can be released to live production monitoring without two independent executive authorizations:
1. Step 13: Final Sign-off by Aravind Chakkaravarthy.
2. Step 14: Production Authorization by Logesh & Heman.
Only when both approvals are recorded in the immutable database history is Step 15 (Production Deployment) unlocked.`
  }
];

export function UserKnowledgeBase() {
  const [selectedCat, setSelectedCat] = useState('all');
  const [search, setSearch] = useState('');
  const [activeArticle, setActiveArticle] = useState<any | null>(null);

  const filteredArticles = KB_ARTICLES.filter(a => {
    const matchesCat = selectedCat === 'all' || a.category === selectedCat;
    const matchesSearch = !search || 
      a.title.toLowerCase().includes(search.toLowerCase()) || 
      a.summary.toLowerCase().includes(search.toLowerCase()) ||
      a.content.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-blue-500" />
          <span>SOC Knowledge Base</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Engineering guidelines, MITRE ATT&CK standards, and governance protocols for detection rule authors.
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search articles across SOC, MITRE, UAT, and Production..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-border bg-card text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Categories */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
        {KB_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCat(cat.id)}
            className={cn(
              "px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-colors",
              selectedCat === cat.id 
                ? "bg-blue-600 text-white shadow-xs" 
                : "bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ARTICLES GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredArticles.map(article => (
          <div
            key={article.id}
            onClick={() => setActiveArticle(article)}
            className="group p-5 rounded-2xl border border-border bg-card hover:bg-muted/30 hover:border-blue-500/50 cursor-pointer shadow-xs transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20">
                  {article.categoryLabel}
                </span>
                <span className="text-[10px] text-muted-foreground">Article ID: {article.id}</span>
              </div>

              <h3 className="text-sm font-bold text-foreground group-hover:text-blue-500 transition-colors">
                {article.title}
              </h3>

              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                {article.summary}
              </p>
            </div>

            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-blue-500 font-semibold">
              <span>Read Article</span>
              <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        ))}
      </div>

      {/* ARTICLE READER MODAL */}
      {activeArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-border bg-card p-6 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500">
                  {activeArticle.categoryLabel}
                </span>
                <h3 className="text-base font-bold text-foreground mt-1">{activeArticle.title}</h3>
              </div>
              <button 
                onClick={() => setActiveArticle(null)}
                className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                &times;
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 text-xs text-foreground/90 space-y-4 custom-scrollbar whitespace-pre-wrap leading-relaxed">
              {activeArticle.content}
            </div>

            <div className="flex items-center justify-end pt-4 border-t border-border">
              <button
                onClick={() => setActiveArticle(null)}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-card border border-border hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
